import React, { useEffect, useState } from "react";
import CalibrationApi from "../../api/CalibrationApi";
import PlateTypeApi from "../../api/PlateTypeApi";
import PlateTypeCaptureProfileApi from "../../api/PlateTypeCaptureProfileApi";
import IconDefaultImagingSettings from "../../Icons/TopCommandPanel/DefaultImagingSettings";
import IconPlateTypeCalibration from "../../Icons/TopCommandPanel/PlateTypeCalibration";
import {
  ELightPath,
  PlateTypeCaptureProfile,
} from "../../Model/CaptureProfile";
import { EModuleType } from "../../Model/EModuleType";
import { EPlateCorner } from "../../Model/EPlateCorner";
import { ImagerCapability } from "../../Model/ImagerCapabilities";
import { Module } from "../../Model/Module";
import {
  DropCalibrationPoint,
  PlateType,
  PlateTypeCalibration,
  WellLimit,
} from "../../Model/PlateType";
import {
  getImagerCapabilities,
  getOneImagerCapabilities,
} from "../../redux/reducers/ImagerCapabilitiesReducer";
import { getModules } from "../../redux/reducers/ModuleReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import WaypointUtil from "../../Utility/WaypointUtil";
import Wait from "../../Wait";
import MtiButton from "../Buttons/MtiButton";
import LayoutContent from "../LayoutContent";
import "../Tools/DebugPage/DebugPage.css";
import ToolsButton from "../Tools/ToolsCommon/ToolsButton";
import ToolsContent from "../Tools/ToolsCommon/ToolsContent";
import ToolsContentComboBox from "../Tools/ToolsCommon/ToolsContentComboBox";
import { getModuleStateById } from "./../../redux/reducers/ModuleStateReducer";
import LightPathUtil from "./../../Utility/LightPathUtil";
import Breadcrumb from "./../Header/Breadcrumb";
import Header from "./../Header/Header";
import { DisplayValue } from "./DefaultImagingSettingViews/DefaultImagingSettingComboBox";
import FocusLevelOptions from "./DefaultImagingSettingViews/FocusLevelOptions";
import NonVisibleDropLocation from "./DefaultImagingSettingViews/NonVisibleDropLocation";
import OpticsAndExposure from "./DefaultImagingSettingViews/OpticsAndExposure";
import VisibleDropLocation from "./DefaultImagingSettingViews/VisibleDropLocation";
import DropPositionTabView from "./PlateCalibrationViews/DropPositionTabView";
import GeneraltabView from "./PlateCalibrationViews/GeneralTabView";
import WellLimitstabView from "./PlateCalibrationViews/WellLimitsTabView";
import { EArmType } from "../../Model/Waypoint";

const CONFIG = "config";
const CALIBRATION = "calibration";
// const IMAGE_MASK = "image-mask";

function getImagerOptions(imagerModules: Module[]): DisplayValue[] {
  return imagerModules.map((module) => {
    return {
      value: module.identifier,
      display: module.name,
    };
  });
}

function getLightpathOptions(
  imagerCapabilities: ImagerCapability
): DisplayValue[] {
  return imagerCapabilities.capabilities.map((capability) => {
    return {
      value: capability.lightpath.toString(),
      display: LightPathUtil.toName(capability.lightpath),
    };
  });
}

// function buildFileSelector() {
//   const fileSelector = document.createElement("input");
//   fileSelector.setAttribute("type", "file");
//   // fileSelector.setAttribute('multiple', 'multiple');
//   return fileSelector;
// }

export default function PlateTypeEditorFloating() {
  const imagerModules = useTypedSelector((state) =>
    getModules(state, EModuleType.IMAGER)
  );
  const moduleStateById = useTypedSelector((state) =>
    getModuleStateById(state)
  );
  const imagerCapabilitiesCollection = useTypedSelector((state) =>
    getImagerCapabilities(state)
  );
  const [selectedImagerIdentifier, setSelectedImagerIdentifier] = useState(
    imagerModules[0].identifier
  );
  const [selectedLightpath, setSelectedLightpath] = useState(
    imagerCapabilitiesCollection.byId[selectedImagerIdentifier]
      .lightpathCollection.allIds[0]
  );
  const imagerCapabilities = useTypedSelector((state) =>
    getOneImagerCapabilities(state, selectedImagerIdentifier)
  );
  const [
    isPlateTypeCaptureProfileUpdated,
    setIsPlateTypeCaptureProfileUpdated,
  ] = useState(false);
  const [isPlateTypeCalibrationUpdated, setIsPlateTypeCalibrationUpdated] =
    useState(false);
  const [plateTypeCalibration, setPlateTypeCalibration] = useState(
    null as PlateTypeCalibration | null
  );
  const [plateTypeCaptureProfiles, setPlateTypeCaptureProfiles] = useState(
    [] as PlateTypeCaptureProfile[]
  );
  const [errorList, setErrorList] = useState([] as string[]);
  // const [extendedMaskShape, setExtendedMaskShape] = useState(EMaskShapeExtended.Rectangle);
  const [waypointNames, setWaypointNames] = useState(null as string[] | null);
  const [wellLimitEnabled, setWellLimitEnabled] = useState(true);
  const moduleState = moduleStateById[selectedImagerIdentifier];
  const imagerState = moduleState.imager;
  if (imagerState == null) {
    throw new Error("Imager state is null");
  }
  const [selectedPlateTypeId, setSelectedPlateTypeId] = useState(-1);
  const [plateTypes, setPlateTypes] = useState([] as PlateType[]);
  const [bodyView, setBodyView] = useState(CALIBRATION);
  const [plateCorner, setPlateCorner] = useState(EPlateCorner.TopLeft);
  const [dropNumber, setDropNumber] = useState(0);
  const lightPathCaptureprofile = plateTypeCaptureProfiles.filter(
    (cp) => cp.lightpath === selectedLightpath
  )[0];
  const visibleCaptureprofile = plateTypeCaptureProfiles.filter(
    (cp) => cp.lightpath === ELightPath.Visible
  )[0];
  const otherCaptureProfiles = plateTypeCaptureProfiles.filter(
    (cp) => cp.lightpath !== selectedLightpath
  );
  const lightpathCapability =
    imagerCapabilities.lightpathCollection.byId[selectedLightpath];

  const setDropCalibration = (dropCalibration: DropCalibrationPoint) => {
    let ptc: PlateTypeCalibration = {
      ...(plateTypeCalibration as PlateTypeCalibration),
    };
    if (ptc.calibrationPoints !== undefined) {
      ptc.calibrationPoints[dropNumber] = dropCalibration;
      onPlateTypeCalibrationUpdated(ptc);
    }
  };

  const setWellLimit = (wellLimit: WellLimit) => {
    onPlateTypeCalibrationUpdated({
      ...(plateTypeCalibration as PlateTypeCalibration),
      wellLimit: wellLimit,
    });
  };

  const onApply = () => {
    if (isPlateTypeCalibrationUpdated && plateTypeCalibration != null) {
      PlateTypeApi.setPlateTypeCalibration(
        selectedPlateType.id,
        selectedImagerIdentifier,
        plateTypeCalibration,
        false
      );
      setIsPlateTypeCalibrationUpdated(false);
    }
    if (isPlateTypeCaptureProfileUpdated) {
      for (let i = 0; i < plateTypeCaptureProfiles.length; i++) {
        PlateTypeCaptureProfileApi.updatePlateTypeCaptureProfile(
          plateTypeCaptureProfiles[i]
        );
      }
      setIsPlateTypeCaptureProfileUpdated(false);
    }
  };

  const onCancel = () => {
    PlateTypeCaptureProfileApi.getPlateTypeCaptureProfiles(
      selectedPlateTypeId,
      selectedImagerIdentifier,
      undefined
    ).then((defaultImagingProfiles) => {
      setPlateTypeCaptureProfiles(defaultImagingProfiles);
    });

    PlateTypeApi.getPlateTypeCalibration(
      selectedPlateTypeId,
      selectedImagerIdentifier,
      true
    ).then((plateTypeCalibration) => {
      setPlateTypeCalibration(plateTypeCalibration);
    });

    setIsPlateTypeCalibrationUpdated(false);
    setIsPlateTypeCaptureProfileUpdated(false);
  };

  const onErrorOccur = (key: string) => {
    let newError: string[] = [...errorList];
    newError.push(key);
    setErrorList(newError);
  };

  const onErrorCleared = (key: string) => {
    let newError: string[] = [...errorList];
    const index = errorList.indexOf(key);
    if (index !== -1) {
      newError.splice(index, 1);
      setErrorList(newError);
    }
  };

  useEffect(() => {
    const preventReload = (e: BeforeUnloadEvent) => {
      if (isPlateTypeCalibrationUpdated || isPlateTypeCaptureProfileUpdated) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = "";
      } else {
        delete e["returnValue"];
      }
    };

    window.addEventListener("beforeunload", preventReload);

    return () => {
      window.removeEventListener("beforeunload", preventReload);
    };
  }, [isPlateTypeCalibrationUpdated, isPlateTypeCaptureProfileUpdated]);

  useEffect(() => {
    setWaypointNames(null);
    CalibrationApi.getWaypoint().then((moduleWaypoints) => {
      var waypointNames = WaypointUtil.getWaypointNames(
        moduleWaypoints,
        selectedImagerIdentifier,
        EArmType.ImagingArm
      );
      setWaypointNames(waypointNames);
    });
  }, [selectedImagerIdentifier]);

  useEffect(() => {
    PlateTypeApi.getPlateTypes().then((plateTypes) => {
      if (plateTypes.length > 0) {
        let id = plateTypes[0].id;
        if (imagerState.plate != null) {
          const plateType = plateTypes.find(
            (plateType) => plateType.id === imagerState.plate?.plateType.id
          );
          if (plateType != null) {
            id = plateType.id;
          }
        }
        setSelectedPlateTypeId(id);
        setPlateTypes(plateTypes);
      }
    });
  }, [imagerState.plate?.barcode]);

  useEffect(() => {
    PlateTypeCaptureProfileApi.getPlateTypeCaptureProfiles(
      selectedPlateTypeId,
      selectedImagerIdentifier,
      undefined
    ).then((defaultImagingProfiles) => {
      setPlateTypeCaptureProfiles(defaultImagingProfiles);
    });

    PlateTypeApi.getPlateTypeCalibration(
      selectedPlateTypeId,
      selectedImagerIdentifier,
      true
    ).then((plateTypeCalibration) => {
      setPlateTypeCalibration(plateTypeCalibration);
    });

    setIsPlateTypeCalibrationUpdated(false);
    setIsPlateTypeCaptureProfileUpdated(false);
  }, [selectedPlateTypeId, selectedImagerIdentifier]);

  const onImagerChanged = (imagerIdentifier: string) => {
    setSelectedImagerIdentifier(imagerIdentifier);
  };

  const onPlateTypeChanged = (plateTypeIdentifier: number) => {
    setSelectedPlateTypeId(plateTypeIdentifier);
  };

  if (waypointNames == null) {
    return <Wait message="Loading waypoint names..." />;
  }

  if (plateTypes.length === 0) {
    return <Wait message="Loading plate types..." />;
  }

  if (plateTypeCalibration == null) {
    return <Wait message="Loading plate type calibration..." />;
  }

  if (plateTypeCaptureProfiles.length === 0) {
    return <Wait message="Loading plate type capture profile..." />;
  }

  const plateTypeOptions = plateTypes.map((plate: PlateType) => {
    return {
      value: plate.id.toString(),
      display: plate.plateTypeName,
    };
  });

  const selectedPlateType = plateTypes.filter(
    (plateType: PlateType) => plateType.id === selectedPlateTypeId
  )[0];
  // const selectedPlateTypeCaptureProfile = plateTypeCaptureProfiles.filter((p) => p.lightpath === selectedLightpath)[0];

  const onPlateTypeCaptureProfileUpdated = (
    captureProfile: PlateTypeCaptureProfile
  ) => {
    setIsPlateTypeCaptureProfileUpdated(true);
    const otherPlateTypeCaptureProfile = plateTypeCaptureProfiles.filter(
      (cp) => cp.lightpath !== captureProfile.lightpath
    );
    setPlateTypeCaptureProfiles([
      ...otherPlateTypeCaptureProfile,
      {
        ...captureProfile,
      },
    ]);
  };

  const onPlateTypeCalibrationUpdated = (
    plateTypeCalibration: PlateTypeCalibration
  ) => {
    setIsPlateTypeCalibrationUpdated(true);
    setPlateTypeCalibration(plateTypeCalibration);
  };

  // const uploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
  //   e.preventDefault();
  //   const reader = new FileReader();
  //   reader.onload = async (e) => {
  //     if (e.target != null) {
  //       const text = e.target.result as string;
  //       PlateTypeApi.importPlateTypeXml(selectedPlateTypeId, selectedImagerIdentifier, text);
  //     }
  //   };
  //   if (e.target?.files != null) {
  //     reader.readAsText(e.target.files[0]);
  //   }
  // };

  const getActivePage = () => {
    switch (bodyView) {
      case CALIBRATION:
        return (
          <div style={{ display: "flex" }}>
            <div style={{ flex: "1" }}>
              <ToolsContent title="General">
                <ToolsContent.Body style={{ padding: 5 }}>
                  <GeneraltabView
                    plateType={selectedPlateType}
                    plateTypeCalibration={plateTypeCalibration}
                    setPlateTypeCalibration={onPlateTypeCalibrationUpdated}
                    waypointNames={waypointNames}
                  />
                </ToolsContent.Body>
              </ToolsContent>
            </div>
            <div style={{ flex: "1" }}>
              <ToolsContent title="Drop Location">
                <ToolsContent.Body style={{ padding: 5 }}>
                  <DropPositionTabView
                    imagerState={imagerState}
                    disabled={
                      imagerState.plate == null ||
                      imagerState.plate.plateType.id !== selectedPlateType.id
                    }
                    plateType={selectedPlateType}
                    imagerIdentifier={selectedImagerIdentifier}
                    plateCorner={plateCorner}
                    setPlateCorner={setPlateCorner}
                    dropNumber={dropNumber}
                    setDropNumber={setDropNumber}
                    dropCalibration={
                      plateTypeCalibration.calibrationPoints[dropNumber]
                    }
                    setDropCalibration={setDropCalibration}
                  />
                </ToolsContent.Body>
              </ToolsContent>
            </div>
            <div style={{ flex: "1" }}>
              <ToolsContent title="Well Limit">
                <ToolsContent.Body style={{ padding: 5 }}>
                  <WellLimitstabView
                    wellLimit={plateTypeCalibration.wellLimit}
                    setWellLimit={setWellLimit}
                    wellLimitEnabled={wellLimitEnabled}
                    setWellLimitEnabled={setWellLimitEnabled}
                  />
                </ToolsContent.Body>
              </ToolsContent>
            </div>
          </div>
        );
      case CONFIG:
        return (
          <div style={{ display: "flex" }}>
            <div style={{ flex: 1 }}>
              <ToolsContent title="Optics and Exposure">
                <ToolsContent.Body style={{ padding: 5 }}>
                  <OpticsAndExposure
                    captureProfile={lightPathCaptureprofile}
                    setCaptureProfile={onPlateTypeCaptureProfileUpdated}
                    lightPath={selectedLightpath}
                    lightpathCapability={lightpathCapability}
                    onErrorOccur={onErrorOccur}
                    onErrorCleared={onErrorCleared}
                  />
                </ToolsContent.Body>
              </ToolsContent>
            </div>
            <div style={{ flex: 1 }}>
              <ToolsContent title="Drop Location">
                <ToolsContent.Body style={{ padding: 5 }}>
                  {selectedLightpath === ELightPath.Visible ? (
                    <VisibleDropLocation
                      nonVisibleCaptureProfiles={otherCaptureProfiles}
                      captureProfile={lightPathCaptureprofile}
                      setCaptureProfile={onPlateTypeCaptureProfileUpdated}
                      lightpathCapability={lightpathCapability}
                      onErrorOccur={onErrorOccur}
                      onErrorCleared={onErrorCleared}
                      plateType={selectedPlateType}
                    />
                  ) : (
                    <NonVisibleDropLocation
                      visibleCaptureProfile={visibleCaptureprofile}
                      captureProfile={lightPathCaptureprofile}
                      setCaptureProfile={onPlateTypeCaptureProfileUpdated}
                      lightPath={selectedLightpath}
                      lightpathCapability={lightpathCapability}
                      plateType={selectedPlateType}
                      onErrorOccur={onErrorOccur}
                      onErrorCleared={onErrorCleared}
                      setDisableAllMenu={() => {}}
                    />
                  )}
                </ToolsContent.Body>
              </ToolsContent>
            </div>
            <div style={{ flex: 1 }}>
              <ToolsContent title="Focus Level">
                <ToolsContent.Body style={{ padding: 5 }}>
                  <FocusLevelOptions
                    lighPathCapability={lightpathCapability}
                    imagerIdentifier={selectedImagerIdentifier}
                    captureProfile={lightPathCaptureprofile}
                    otherCaptureProfiles={otherCaptureProfiles}
                    setCaptureProfile={onPlateTypeCaptureProfileUpdated}
                    lightPath={selectedLightpath}
                    onErrorOccur={onErrorOccur}
                    onErrorCleared={onErrorCleared}
                  />
                </ToolsContent.Body>
              </ToolsContent>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="debug-layout">
      <div className="debug-left">
        <div className="debug-sidebar">
          <svg
            style={{
              margin: 21,
            }}
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6 0C2.68629 0 0 2.68629 0 6V42C0 45.3137 2.68629 48 6 48H42C45.3137 48 48 45.3137 48 42V6C48 2.68629 45.3137 0 42 0H6ZM22.3612 8.24086L21.9037 5.12381C22.4936 5.05595 23.0824 5.01626 23.6687 5.00391L23.6304 8.15463C23.2086 8.16626 22.7853 8.19485 22.3612 8.24086ZM18.7628 5.75625L19.7362 8.75194C20.2021 8.61999 20.6695 8.51031 21.1371 8.42228L20.6795 5.30524C20.0397 5.42144 19.4001 5.57142 18.7628 5.75625ZM17.4944 9.56977L16.0834 6.75308C16.5712 6.52905 17.0722 6.32385 17.5858 6.13868L18.5592 9.13437C18.1966 9.26772 17.8415 9.41305 17.4944 9.56977ZM13.6262 8.10944L15.4361 10.6887C15.7449 10.4899 16.062 10.301 16.3871 10.1225L14.9761 7.3058C14.5125 7.5564 14.0623 7.82465 13.6262 8.10944ZM13.6609 12.0166L11.5098 9.71404C11.8649 9.40329 12.2327 9.10483 12.6127 8.81956L14.4226 11.3988C14.1616 11.5974 13.9076 11.8034 13.6609 12.0166ZM9.44068 11.8317L11.9224 13.773C12.188 13.4592 12.4663 13.155 12.7568 12.8615L10.6057 10.5589C10.1973 10.9664 9.80867 11.3912 9.44068 11.8317ZM10.4901 15.7559L7.74001 14.2181C8.0316 13.7344 8.34473 13.2634 8.67862 12.8065L11.1603 14.7479C10.9241 15.0755 10.7005 15.4118 10.4901 15.7559ZM6.35033 17.0165L9.3152 18.0842C9.4875 17.6599 9.67793 17.2432 9.88586 16.8355L7.13577 15.2977C6.84598 15.8571 6.58377 16.4308 6.35033 17.0165ZM8.56593 20.4374L5.47079 19.8414C5.59883 19.2799 5.75207 18.7255 5.92965 18.1798L8.89453 19.2476C8.76985 19.6392 8.66017 20.0361 8.56593 20.4374ZM5.01489 23.208L8.16723 23.2451C8.19471 22.7094 8.24938 22.1777 8.33029 21.6518L5.23514 21.0557C5.1213 21.7652 5.04735 22.4837 5.01489 23.208ZM8.23815 25.7504L5.11932 26.2088C5.05163 25.6193 5.01215 25.0308 5 24.4448L8.15236 24.4819C8.16384 24.9035 8.19228 25.3266 8.23815 25.7504ZM8.74851 28.3739L5.75091 29.3479L5.74644 29.3324C5.56175 28.6955 5.41141 28.0565 5.29538 27.417L8.41421 26.9586L8.41715 26.9742L8.4192 26.9739C8.5071 27.4412 8.61667 27.9082 8.74851 28.3739ZM24.9061 5.01831L24.8678 8.16904C25.4038 8.19631 25.9358 8.25075 26.4619 8.33141L27.0594 5.23765C26.3496 5.12413 25.6308 5.05048 24.9061 5.01831ZM27.6768 8.56649L28.2743 5.47272C28.836 5.60049 29.3906 5.75344 29.9365 5.93074L28.8672 8.89448C28.4754 8.77001 28.0783 8.66053 27.6768 8.56649ZM31.1003 6.35077L30.031 9.3145C30.4554 9.48656 30.8722 9.67673 31.2801 9.8844L32.8197 7.13516C32.2601 6.84572 31.6862 6.58388 31.1003 6.35077ZM32.3601 10.4879L33.8996 7.73869C34.3834 8.02994 34.8546 8.34275 35.3116 8.6763L33.3683 11.1575C33.0406 10.9214 32.7042 10.6981 32.3601 10.4879ZM36.2866 9.43759L34.3434 11.9188C34.6573 12.1842 34.9615 12.4622 35.2551 12.7524L37.5596 10.6016C37.1521 10.1935 36.7272 9.80524 36.2866 9.43759ZM36.1001 13.6557L38.4046 11.5049C38.7154 11.8597 39.0139 12.2272 39.2991 12.6069L36.7179 14.4168C36.5193 14.156 36.3132 13.9023 36.1001 13.6557ZM40.0092 13.6196L37.428 15.4295C37.6268 15.7381 37.8157 16.055 37.9941 16.3799L40.8128 14.9685C40.5622 14.5052 40.294 14.0554 40.0092 13.6196ZM38.5467 17.4864L41.3654 16.075C41.5893 16.5625 41.7944 17.0632 41.9795 17.5765L38.9819 18.5504C38.8487 18.188 38.7034 17.8332 38.5467 17.4864ZM42.3617 18.7527L39.3641 19.7267C39.496 20.1924 39.6055 20.6594 39.6934 21.1267L42.8122 20.6683C42.6962 20.0288 42.5464 19.3896 42.3617 18.7527ZM39.8745 22.3502L42.9933 21.8918C43.061 22.4813 43.1005 23.0698 43.1126 23.6558L39.9603 23.6187C39.9488 23.1972 39.9204 22.774 39.8745 22.3502ZM43.0977 24.8926L39.9454 24.8555C39.9179 25.3912 39.8633 25.9229 39.7824 26.4488L42.8775 27.0449C42.9913 26.3354 43.0653 25.6169 43.0977 24.8926ZM39.5467 27.6632L42.6418 28.2592C42.5138 28.8207 42.3606 29.3751 42.183 29.9208L39.2181 28.853C39.3428 28.4614 39.4525 28.0645 39.5467 27.6632ZM41.7623 31.0841L38.7974 30.0164C38.6251 30.4407 38.4347 30.8574 38.2268 31.2651L40.9769 32.8029C41.2666 32.2435 41.5288 31.6698 41.7623 31.0841ZM37.6225 32.3448L40.3726 33.8825C40.081 34.3661 39.7679 34.8372 39.434 35.2941L36.9523 33.3528C37.1885 33.0251 37.4121 32.6888 37.6225 32.3448ZM38.6719 36.2689L36.1902 34.3276C35.9246 34.6414 35.6463 34.9456 35.3558 35.2391L37.5069 37.5417C37.9153 37.1342 38.3039 36.7094 38.6719 36.2689ZM34.4517 36.084L36.6028 38.3866C36.2477 38.6973 35.8799 38.9958 35.4999 39.281L33.69 36.7018C33.951 36.5032 34.205 36.2972 34.4517 36.084ZM34.4864 39.9912L32.6765 37.4119C32.3677 37.6107 32.0506 37.7996 31.7255 37.9781L33.1365 40.7948C33.6001 40.5442 34.0503 40.2759 34.4864 39.9912ZM30.6182 38.5308L32.0292 41.3475C31.5414 41.5716 31.0404 41.7767 30.5268 41.9619L29.5534 38.9662C29.916 38.8329 30.2711 38.6876 30.6182 38.5308ZM29.3498 42.3443L28.3764 39.3487C27.9105 39.4806 27.4431 39.5903 26.9755 39.6783L27.4331 42.7954C28.0729 42.6792 28.7125 42.5292 29.3498 42.3443ZM25.7514 39.8597L25.7522 39.8657L25.7614 39.8647L26.219 42.9817C25.6291 43.0496 25.0403 43.0893 24.454 43.1016L24.4541 43.0965L24.4439 43.0967L24.4822 39.946C24.904 39.9343 25.3273 39.9057 25.7514 39.8597ZM23.2447 39.9362L23.2448 39.9315C22.7088 39.9043 22.1768 39.8498 21.6507 39.7692L21.0532 42.8629L21.064 42.8647L21.0633 42.8681C21.7731 42.9816 22.4919 43.0553 23.2167 43.0874L23.2549 39.9367L23.2447 39.9362ZM20.4358 39.5341L20.4353 39.5368L20.446 39.5393L19.8485 42.633C19.2868 42.5053 18.7321 42.3523 18.1862 42.175L18.1868 42.1733L18.1761 42.1698L19.2454 39.2061C19.6372 39.3306 20.0343 39.44 20.4358 39.5341ZM18.0817 38.7861C17.6572 38.614 17.2404 38.4238 16.8325 38.2162L15.293 40.9654L15.3757 41.008C15.9123 41.2826 16.4619 41.532 17.0224 41.755L18.0918 38.7912L18.0817 38.7861ZM14.2134 40.3612L14.2232 40.3671L15.7627 37.6178C15.4186 37.4076 15.0822 37.1843 14.7545 36.9483L14.7534 36.9496L14.7443 36.9431L12.801 39.4243C13.258 39.7578 13.7296 40.0699 14.2134 40.3612ZM11.8276 38.661L11.826 38.663C11.3854 38.2953 10.9605 37.9071 10.553 37.499L12.8575 35.3482L12.8652 35.3557L12.8677 35.3533C13.1613 35.6436 13.4655 35.9216 13.7794 36.187L11.8362 38.6682L11.8276 38.661ZM9.70803 36.5957L9.71111 36.5928L9.71817 36.6009L12.0227 34.45C11.8095 34.2035 11.6035 33.9497 11.4049 33.6889L11.4008 33.6918L11.3947 33.6837L8.8135 35.4937C9.09877 35.8734 9.39726 36.2409 9.70803 36.5957ZM8.10803 34.4777L8.10338 34.481C7.81862 34.0452 7.5504 33.5953 7.29984 33.132L10.1185 31.7207L10.1229 31.7287L10.1287 31.7259C10.3071 32.0508 10.496 32.3677 10.6948 32.6762L8.11354 34.4862L8.10803 34.4777ZM6.74724 32.0256L6.75357 32.0224L6.7574 32.0307L9.57607 30.6194C9.41939 30.2725 9.27412 29.9177 9.14084 29.5553L9.13348 29.5577L9.1307 29.5501L6.1331 30.5241C6.31818 31.0374 6.52328 31.5381 6.74724 32.0256ZM16.8096 17.0665L12.4657 34.21L19.4715 22.9626V22.8424H19.5464L22.2147 18.5586L32.1294 15.6406L23.1763 12.9547L16.8096 17.0665ZM13.2284 35.1384L19.8433 25.2507H22.8509V28.6301H25.2592V25.2507H26.5877L29.1782 28.1749L28.1502 34.21L13.2284 35.1384ZM25.2592 19.8398V20.7787L26.9153 22.8424H28.6385V24.9897L31.0351 27.976L29.344 33.6131L35.6443 27.3459L33.2568 16.337L25.2592 19.8398ZM22.8509 22.8424V21.0323L22.7452 20.913L21.4544 22.8424H22.8509ZM23.4201 20.2372H24.6899V23.4116H27.8643V24.6814H24.6899V27.8558H23.4201V24.6814H20.2457V23.4116H23.4201V20.2372Z"
              fill="#F7A246"
            />
          </svg>
        </div>
      </div>

      <div id="CONTENT">
        <LayoutContent>
          <LayoutContent.Header>
            <Header>
              <Breadcrumb
                items={[
                  {
                    title: "Plate Type Editor Floating",
                    to: "/plate-type-editor-floating",
                    disabled: false,
                  },
                ]}
              />
              <Header.RightSide>
                <MtiButton
                  size="large"
                  disabled={
                    !isPlateTypeCalibrationUpdated &&
                    !isPlateTypeCaptureProfileUpdated
                  }
                  style={{
                    margin: "auto 10px",
                  }}
                  onClick={onCancel}
                >
                  CANCEL
                </MtiButton>
                <MtiButton
                  size="large"
                  disabled={
                    !isPlateTypeCalibrationUpdated &&
                    !isPlateTypeCaptureProfileUpdated
                  }
                  style={{
                    margin: "auto 10px",
                  }}
                  onClick={onApply}
                >
                  APPLY
                </MtiButton>
              </Header.RightSide>
            </Header>
          </LayoutContent.Header>

          <LayoutContent.Content>
            <div style={{ display: "flex" }}>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    paddingLeft: 20,
                    paddingTop: 10,
                    display: "flex",
                    justifyContent: "space-around",
                    flexDirection: "column",
                  }}
                >
                  <ToolsButton
                    title={"Plate Type Calibration"}
                    active={bodyView === CALIBRATION}
                    icon={<IconPlateTypeCalibration />}
                    onClick={() => setBodyView(CALIBRATION)}
                  />
                  <ToolsButton
                    title={"Default Imaging Settings"}
                    active={bodyView === CONFIG}
                    icon={<IconDefaultImagingSettings />}
                    onClick={() => setBodyView(CONFIG)}
                  />
                </div>
              </div>
              <div
                style={{ flex: 4, display: "flex", flexDirection: "column" }}
              >
                <div
                  style={{
                    border: "1px solid #E0E0E0",
                    padding: "20px 15px",
                    margin: 10,
                    display: "flex",
                  }}
                >
                  <ToolsContentComboBox
                    label="Plate Type: "
                    displayValues={plateTypeOptions}
                    onChange={(value) =>
                      onPlateTypeChanged(Number.parseInt(value))
                    }
                    style={{ marginRight: 10 }}
                  />
                  <ToolsContentComboBox
                    label="Imager: "
                    displayValues={getImagerOptions(imagerModules)}
                    onChange={(identifier) => onImagerChanged(identifier)}
                    style={{ marginRight: 10 }}
                  />
                  <ToolsContentComboBox
                    label="Light Path: "
                    displayValues={getLightpathOptions(imagerCapabilities)}
                    onChange={(lp) => setSelectedLightpath(Number.parseInt(lp))}
                    style={{ marginRight: 10 }}
                  />
                </div>
                <div
                  style={{
                    border: "1px solid #E0E0E0",
                    padding: "20px 15px",
                    margin: 10,
                    height: "76vh",
                  }}
                >
                  {getActivePage()}
                </div>
              </div>
            </div>
          </LayoutContent.Content>
        </LayoutContent>
      </div>
    </div>
  );
}
